import React, { useEffect, useState } from 'react';

import { Loader } from '@/components/Loader';
import { Modal } from '@/components/Modal';
import { useAxios } from '@/hooks/useAxios';
import { queryClient } from '@/queryclient';
import { Technology } from '@/types';
import { cn } from '@/utils/classname';

const DEFAULT_GOAL = `Hunt for undiscovered 0-day vulnerabilities not yet assigned a CVE. Target CVSS 10.0 severity: network-exploitable, requires no authentication, no user interaction, and achieves remote code execution or complete system compromise. Prioritize weaponizable flaws with high EPSS potential—memory corruption, command injection, deserialization, and authentication bypasses that grant immediate attacker control. Ignore low-impact findings. Focus exclusively on critical, exploit-ready vulnerabilities.`;

interface ConstantineModalProps {
  open: boolean;
  onClose: () => void;
  technology: Technology | null;
}

export const ConstantineModal: React.FC<ConstantineModalProps> = ({
  open,
  onClose,
  technology,
}) => {
  const axios = useAxios();
  const [repoUrl, setRepoUrl] = useState('');
  const [goal, setGoal] = useState('');
  const [isGuessing, setIsGuessing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // When modal opens with a technology, guess the repo URL
  useEffect(() => {
    if (open && technology) {
      setRepoUrl('');
      setGoal('');
      setError('');
      setSuccess('');
      guessRepo();
    }
  }, [open, technology?.key]);

  const guessRepo = async () => {
    if (!technology) return;
    setIsGuessing(true);
    setError('');
    try {
      const { data } = await axios.post('/osint/guess-repo', {
        cpe: technology.cpe,
        technology_name:
          technology.name || `${technology.vendor} ${technology.product}`,
      });
      setRepoUrl(data.repo_url || '');
    } catch (err) {
      console.error('Failed to guess repo:', err);
      setError('Could not guess repository URL. Please enter it manually.');
    } finally {
      setIsGuessing(false);
    }
  };

  const handleSubmit = async () => {
    if (!repoUrl.trim()) {
      setError('Repository URL is required');
      return;
    }

    setIsSubmitting(true);
    setError('');
    try {
      await axios.post('/osint/submit', {
        repo_url: repoUrl.trim(),
        technology_key: technology?.key || undefined,
        goal: goal.trim() || DEFAULT_GOAL,
      });

      // Invalidate OSINT job queries to refetch and show the new queued job
      queryClient.invalidateQueries({
        predicate: query => {
          const queryKey = query.queryKey as string[];
          return (
            queryKey[0] === 'MY' &&
            queryKey[1] === 'job' &&
            queryKey.includes('osint')
          );
        },
        refetchType: 'active',
      });

      // Also invalidate repository queries in case new repos were discovered
      queryClient.invalidateQueries({
        predicate: query => {
          const queryKey = query.queryKey as string[];
          return (
            queryKey[0] === 'OSINT' &&
            queryKey[1] === 'repos' &&
            queryKey[2] === 'technology'
          );
        },
        refetchType: 'active',
      });

      setSuccess(
        'Constantine scan initiated. Check the Exploits tab for results.'
      );
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      const message =
        axiosErr?.response?.data?.message ||
        'Failed to submit. Please try again.';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setRepoUrl('');
      setGoal('');
      setError('');
      setSuccess('');
      onClose();
    }
  };

  if (!open || !technology) return null;

  return (
    <Modal
      size="xl"
      title="Research with Constantine"
      subtitle={`Hunt for 0-day vulnerabilities in ${technology.name || technology.product}`}
      open={open}
      onClose={handleClose}
      disableAutoFocus
      footer={{
        text: isSubmitting ? 'Submitting...' : 'Make Me Famous',
        onClick: handleSubmit,
        disabled: !repoUrl.trim() || isSubmitting || !!success,
      }}
    >
      <div className="space-y-4">
        {error && (
          <div
            className="bg-chariot-status-critical/10 text-chariot-status-critical rounded-md p-2 text-sm"
            role="alert"
          >
            {error}
          </div>
        )}

        {success && (
          <div
            className="bg-chariot-status-positive/10 text-chariot-status-positive rounded-md p-2 text-sm"
            role="status"
          >
            {success}
          </div>
        )}

        <div>
          <label
            htmlFor="constantine-repo-url"
            className="text-chariot-text-primary mb-1 block text-sm font-medium"
          >
            Repository URL
          </label>
          <div className="relative">
            <input
              id="constantine-repo-url"
              type="url"
              value={repoUrl}
              onChange={e => {
                setRepoUrl(e.target.value);
                if (error) setError('');
              }}
              placeholder={
                isGuessing
                  ? 'Guessing repository...'
                  : 'https://github.com/org/repo'
              }
              disabled={isGuessing || isSubmitting || !!success}
              className={cn(
                'border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary w-full rounded-md border px-3 py-2 pr-9 text-sm',
                'focus:border-chariot-layer-primary focus:ring-chariot-layer-primary focus:ring-1 focus:outline-hidden',
                'disabled:cursor-not-allowed disabled:opacity-50'
              )}
            />
            {isGuessing && (
              <div className="absolute top-1/2 right-3 -translate-y-1/2">
                <Loader isLoading type="spinner" />
              </div>
            )}
          </div>
        </div>

        <div>
          <label
            htmlFor="constantine-goal"
            className="text-chariot-text-primary mb-1 block text-sm font-medium"
          >
            Research Goal
          </label>
          <textarea
            id="constantine-goal"
            rows={8}
            value={goal}
            onChange={e => setGoal(e.target.value)}
            placeholder={DEFAULT_GOAL}
            disabled={isSubmitting || !!success}
            className={cn(
              'border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary w-full rounded-md border px-3 py-2 text-sm',
              'focus:border-chariot-layer-primary focus:ring-chariot-layer-primary focus:ring-1 focus:outline-hidden',
              'disabled:cursor-not-allowed disabled:opacity-50',
              'resize-y'
            )}
          />
          {!goal.trim() && (
            <button
              type="button"
              onClick={() => setGoal(DEFAULT_GOAL)}
              className="text-chariot-layer-primary mt-1 text-xs hover:underline"
            >
              Use default prompt
            </button>
          )}
          <p className="text-chariot-text-secondary mt-1 text-xs">
            {!goal.trim()
              ? 'Describe what vulnerabilities to hunt for, or use the default prompt above.'
              : goal === DEFAULT_GOAL
                ? 'Default prompt targets critical 0-day vulnerabilities.'
                : 'Your custom goal will focus the analysis.'}
          </p>
        </div>
      </div>
    </Modal>
  );
};
